import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Check, AlertCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { CameraScreen } from '../../components/CameraScreen';
import { ReportProblemModal } from '../../components/ReportProblemModal';
type Room = {
  id: string;
  nameKey: string;
  photoUrl: string | null;
  referencePhotoUrl: string;
};
export function BeforePhotosPage() {
  const {
    t
  } = useLanguage();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<Room[]>([{
    id: 'soggiorno',
    nameKey: 'soggiorno',
    photoUrl: null,
    referencePhotoUrl: 'https://a0.muscache.com/im/pictures/prohost-api/Hosting-1353673811838304349/original/142376af-a8ad-457c-a3af-4b264a5e291c.jpeg?aki_policy=xx_large'
  }, {
    id: 'cucina',
    nameKey: 'cucina',
    photoUrl: null,
    referencePhotoUrl: 'https://a0.muscache.com/im/pictures/hosting/Hosting-1589863564105425260/original/22e98b9a-4848-48d2-8bc9-f75adab5a84d.jpeg?im_w=1200'
  }, {
    id: 'bagno',
    nameKey: 'bagno',
    photoUrl: null,
    referencePhotoUrl: 'https://a0.muscache.com/im/pictures/hosting/Hosting-1589863564105425260/original/8da17c5b-c2d1-4b3e-aa33-a0df921816fa.jpeg?aki_policy=xx_large'
  }, {
    id: 'camera1',
    nameKey: 'camera1',
    photoUrl: null,
    referencePhotoUrl: 'https://a0.muscache.com/im/pictures/hosting/Hosting-1589863564105425260/original/731e18a0-32d0-464a-ba59-ab8afb80ae73.jpeg?aki_policy=xx_large'
  }, {
    id: 'camera2',
    nameKey: 'camera2',
    photoUrl: null,
    referencePhotoUrl: 'https://a0.muscache.com/im/pictures/hosting/Hosting-1589863564105425260/original/c900d0e5-5343-48d7-aad9-a831c90712e7.jpeg?aki_policy=xx_large'
  }, {
    id: 'ingresso',
    nameKey: 'ingresso',
    photoUrl: null,
    referencePhotoUrl: 'https://a0.muscache.com/im/pictures/hosting/Hosting-U3RheVN1cHBseUxpc3Rpbmc6MTM0MDM1MzQyMDY5ODk1MTY1NA%3D%3D/original/bfe5b7dc-812b-4978-a225-9a4a74e37e4c.jpeg?aki_policy=xx_large'
  }]);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const takenCount = rooms.filter(r => r.photoUrl !== null).length;
  const totalCount = rooms.length;
  const progress = takenCount / totalCount * 100;
  const isComplete = takenCount === totalCount;
  const handleOpenCamera = (room: Room) => {
    setSelectedRoom(room);
    setCameraOpen(true);
  };
  const handleCapturePhoto = (photoUrl: string) => {
    if (selectedRoom) {
      setRooms(prev => prev.map(room => room.id === selectedRoom.id ? {
        ...room,
        photoUrl
      } : room));
    }
    setCameraOpen(false);
    setSelectedRoom(null);
  };
  const handleCancelCamera = () => {
    setCameraOpen(false);
    setSelectedRoom(null);
  };
  const handleReportProblem = (problemType: string) => {
    console.log('Problem reported:', problemType);
    setReportModalOpen(false);
  };
  const handleStartCleaning = () => {
    if (isComplete) {
      navigate('/fornitore/cleaning-in-progress');
    }
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-662">
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 pb-32" data-id="element-663">
        {/* Page Header */}
        <div className="mb-12" data-id="element-664">
          <h1 className="text-4xl font-bold text-navy mb-3" data-id="element-665">
            {t.fornitore.beforePhotos.title}
          </h1>
          <p className="text-base text-gray-600 flex items-center gap-2" data-id="element-666">
            <span className="font-medium" data-id="element-667">Via Roma 123, Appartamento 4B</span>
          </p>
        </div>

        {/* Progress Section */}
        <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 mb-12" data-id="element-668">
          <div className="flex justify-between items-end mb-4" data-id="element-669">
            <span className="text-base font-medium text-gray-500" data-id="element-670">
              {t.fornitore.beforePhotos.subtitle}
            </span>
            <span className="text-xl font-bold text-teal-deep" data-id="element-671">
              {t.fornitore.beforePhotos.progress.replace('{current}', takenCount.toString()).replace('{total}', totalCount.toString())}
            </span>
          </div>
          <div className="h-4 bg-gray-100 rounded-full overflow-hidden" data-id="element-672">
            <motion.div className="h-full bg-teal-deep rounded-full" initial={{
            width: 0
          }} animate={{
            width: `${progress}%`
          }} transition={{
            duration: 0.5,
            ease: 'easeOut'
          }} data-id="element-673" />
          </div>
        </div>

        {/* Photo Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-id="element-674">
          {rooms.map(room => <div key={room.id} className="space-y-3" data-id="element-675">
              <motion.div whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => handleOpenCamera(room)} className="cursor-pointer" data-id="element-676">
                <Card className={`relative aspect-[4/3] overflow-hidden group transition-all duration-300 ${room.photoUrl ? 'border-2 border-teal-deep shadow-md' : 'bg-white border border-gray-200'}`} data-id="element-677">
                  <AnimatePresence mode="wait" data-id="element-678">
                    {room.photoUrl ? <motion.div key="taken" initial={{
                  opacity: 0,
                  scale: 0.8
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} className="absolute inset-0" data-id="element-679">
                        <img src={room.photoUrl} alt="Taken" className="w-full h-full object-cover" data-id="element-680" />
                        <div className="absolute top-2 right-2 bg-teal-deep text-white p-2 rounded-full shadow-lg" data-id="element-681">
                          <Check className="w-5 h-5" data-id="element-682" />
                        </div>
                      </motion.div> : <motion.div key="empty" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} className="absolute inset-0 flex flex-col items-center justify-center text-gray-400" data-id="element-683">
                        <Camera className="w-10 h-10 mb-3" data-id="element-684" />
                        <span className="text-base font-medium" data-id="element-685">
                          {t.fornitore.beforePhotos.takePhoto}
                        </span>
                      </motion.div>}
                  </AnimatePresence>
                </Card>
              </motion.div>
              <p className="text-center font-medium text-base text-gray-700" data-id="element-686">
                {t.fornitore.beforePhotos.rooms[room.nameKey as keyof typeof t.fornitore.beforePhotos.rooms]}
              </p>
            </div>)}
        </div>
      </main>

      {/* Fixed Bottom Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 md:p-6 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-40" data-id="element-687">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4" data-id="element-688">
          <button onClick={() => setReportModalOpen(true)} className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-lg text-red-600 hover:bg-red-50 transition-colors border-2 border-red-500" data-id="element-689">
            <AlertCircle className="w-6 h-6 text-red-600" data-id="element-690" />
            <span className="hidden md:inline text-red-600" data-id="element-691">
              {t.fornitore.beforePhotos.reportProblem}
            </span>
            <span className="md:hidden text-red-600" data-id="element-692">Segnala</span>
          </button>

          <Button onClick={handleStartCleaning} disabled={!isComplete} className={`text-lg ${isComplete ? '' : 'opacity-40 cursor-not-allowed'}`} data-id="element-693">
            {t.fornitore.beforePhotos.startCleaning}
            <ArrowRight className="w-5 h-5 ml-2" data-id="element-694" />
          </Button>
        </div>
      </div>

      {/* Camera Screen */}
      {selectedRoom && <CameraScreen isOpen={cameraOpen} roomName={t.fornitore.beforePhotos.rooms[selectedRoom.nameKey as keyof typeof t.fornitore.beforePhotos.rooms]} referencePhotoUrl={selectedRoom.referencePhotoUrl} onCapture={handleCapturePhoto} onCancel={handleCancelCamera} data-id="element-695" />}

      {/* Report Problem Modal */}
      <ReportProblemModal isOpen={reportModalOpen} onClose={() => setReportModalOpen(false)} onSubmit={handleReportProblem} data-id="element-696" />
    </div>;
}